import React, { useState, useEffect, useMemo } from 'react';
import { IoCloseOutline, IoDownloadOutline, IoCheckmarkCircle, IoChevronForward, IoShieldCheckmarkOutline } from 'react-icons/io5';
import { REGIONS_DATA, fetchCurrencies, CURRENCIES, getAllCountries } from '../utils/regionData';
import { getRwLogoPng } from '../utils/pdfInvoiceLogo';
import axios from 'axios';

export const FULL_COUNTRIES_LIST = getAllCountries();
REGIONS_DATA[0].countries = FULL_COUNTRIES_LIST;

const PurchaseModal = ({ show, onClose, report, initialFormat = 'pdf', initialCurrency = CURRENCIES[0] }) => {
    const [step, setStep] = useState(1);

    // Determine available regions based on report pricing options
    const availableRegions = useMemo(() => {
        if (!report?.pricingOptions) return REGIONS_DATA;
        const pricing = report.pricingOptions;
        if (!pricing.enableRegional) {
            return REGIONS_DATA.filter(r => r.id === 'global');
        }
        const enabledNames = pricing.regions?.map(r => r.region).filter(Boolean) || [];
        const filtered = REGIONS_DATA.filter(r =>
            r.id === 'global' || enabledNames.includes(r.name)
        );
        return filtered.length > 0 ? filtered : REGIONS_DATA;
    }, [report]);

    const [selectedRegion, setSelectedRegion] = useState(availableRegions[0] || REGIONS_DATA[0]);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedFormat, setSelectedFormat] = useState(initialFormat);
    const [availableCurrencies, setAvailableCurrencies] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useState(initialCurrency);

    // Fetch currencies on component mount
    useEffect(() => {
        const load = async () => {
            try {
                const fetched = await fetchCurrencies();
                setAvailableCurrencies(fetched);
                // Set default selected currency: USD or first fetched
                if (!selectedCurrency && fetched.length > 0) {
                    const usdCurrency = fetched.find(c => c.code === 'USD');
                    setSelectedCurrency(usdCurrency || fetched[0]);
                }
            } catch (e) {
                console.warn('Failed to load currencies', e);
            }
        };
        load();
    }, [selectedCurrency]);

    // Telemetry Shield: Silence Razorpay's aggressive localhost probes on port 7071
    useEffect(() => {
        if (!show) return;

        const originalDescriptor = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src');
        if (!originalDescriptor) return;

        Object.defineProperty(HTMLImageElement.prototype, 'src', {
            set: function(value) {
                if (typeof value === 'string' && value.includes('localhost:7071')) {
                    // console.debug('Suppressed Razorpay probe:', value);
                    return;
                }
                originalDescriptor.set.call(this, value);
            },
            get: function() {
                return originalDescriptor.get.call(this);
            },
            configurable: true
        });

        return () => {
            Object.defineProperty(HTMLImageElement.prototype, 'src', originalDescriptor);
        };
    }, [show]);

    useEffect(() => {
        if (show) {
            setStep(1);
            setSuccess(false);
            setError('');
            setIgnoreWarning(false);
            
            if (initialFormat) setSelectedFormat(initialFormat);
            if (initialCurrency) setSelectedCurrency(initialCurrency);
            // Ensure region/country are set to defaults
            setSelectedRegion(availableRegions[0] || REGIONS_DATA[0]);
            setSelectedCountry('');

            


            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [show, report, initialFormat, initialCurrency, availableRegions]);

    const [finalPrice, setFinalPrice] = useState(0);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        businessEmail: '',
        companyName: '',
        phoneNumber: '',
        billingAddress: '',
        message: ''
    });

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [alreadyPurchased, setAlreadyPurchased] = useState(null);
    const [ignoreWarning, setIgnoreWarning] = useState(false);

    useEffect(() => {
        setIgnoreWarning(false);
        if (!report || !formData.businessEmail) {
            setAlreadyPurchased(null);
            return;
        }

        const purchasedKey = 'rwire_purchased_reports';
        const existingPurchases = JSON.parse(localStorage.getItem(purchasedKey) || '[]');
        
        const previous = existingPurchases.find(p => 
            p.reportId === report.id && 
            p.format === selectedFormat && 
            p.email?.toLowerCase().trim() === formData.businessEmail.toLowerCase().trim()
        );
        
        setAlreadyPurchased(previous || null);
    }, [report, selectedFormat, formData.businessEmail]);


    useEffect(() => {
        if (!report) return;

        let basePrice = 0;
        let pricing = report.pricingOptions || report.pricing_options || {};
        if (typeof pricing === 'string') {
            try { pricing = JSON.parse(pricing); } catch (e) { pricing = {}; }
        }

        if (pricing.enableRegional && selectedRegion.id !== 'global') {
            const regionalPrice = pricing.regions?.find(r => r.region === selectedRegion.name);
            if (regionalPrice) {
                if (selectedFormat === 'excel_only') basePrice = parseFloat(regionalPrice.excelOnly);
                else if (selectedFormat === 'pdf') basePrice = parseFloat(regionalPrice.singleUser);
                else if (selectedFormat === 'excel') basePrice = parseFloat(regionalPrice.multiUser);
                else basePrice = parseFloat(regionalPrice.enterprise);
            } else {
                applyGlobalFallback();
            }
        } else {
            applyGlobalFallback();
        }

        function applyGlobalFallback() {
            const global = pricing.global || {};
            const getValidPrice = (val, fallback) => {
                const parsed = parseFloat(val);
                return (!isNaN(parsed) && parsed > 0) ? parsed : fallback;
            };
            const reportBasePrice = parseFloat(report.price || 0);
            if (selectedFormat === 'excel_only') {
                basePrice = getValidPrice(global.excelOnly, reportBasePrice > 0 ? reportBasePrice * 0.5 : 0);
            } else if (selectedFormat === 'pdf') {
                basePrice = getValidPrice(global.singleUser, reportBasePrice);
            } else if (selectedFormat === 'excel') {
                basePrice = getValidPrice(global.multiUser, reportBasePrice > 0 ? reportBasePrice * 1.5 : 0);
            } else {
                basePrice = getValidPrice(global.enterprise, reportBasePrice > 0 ? reportBasePrice * 2 : 0);
            }
        }

        let options = report.pricingOptions || report.pricing_options || {};
        if (typeof options === 'string') {
            try { options = JSON.parse(options); } catch (e) { options = {}; }
        }
        const globalOptions = options.global || {};
        const reportCurrencyCode = globalOptions.currency || 'USD';

        // Find report base currency rate from loaded list
        const reportBaseCurrency = availableCurrencies.find(c => c.code === reportCurrencyCode) || { rate: 1 };

        // Converted price = price * (userSelectedCurrencyRate / reportBaseCurrencyRate)
        const convertedPrice = basePrice * ((selectedCurrency?.rate || 1) / (reportBaseCurrency.rate || 1));
        setFinalPrice(Math.round(convertedPrice));
    }, [report, selectedRegion, selectedFormat, selectedCurrency, availableCurrencies]);

    const [orderData, setOrderData] = useState(null);
    const [downloadLoading, setDownloadLoading] = useState(false);

    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            if (window.Razorpay) {
                resolve(true);
                return;
            }
            const scriptId = 'razorpay-checkout-js';
            if (document.getElementById(scriptId)) {
                // Script is already injecting, wait for it
                const interval = setInterval(() => {
                    if (window.Razorpay) {
                        clearInterval(interval);
                        resolve(true);
                    }
                }, 100);
                return;
            }
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.async = true;
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const loadJsPDF = () => {
        return new Promise((resolve) => {
            if (window.jspdf) { resolve(true); return; }
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
            script.onload = () => {
                // Load autotable plugin after jspdf
                const autoTableScript = document.createElement('script');
                autoTableScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.31/jspdf.plugin.autotable.min.js';
                autoTableScript.onload = () => resolve(true);
                autoTableScript.onerror = () => resolve(false);
                document.body.appendChild(autoTableScript);
            };
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handleDownload = async () => {
        if (!orderData?.orderId) return;
        setDownloadLoading(true);

        try {
            const loaded = await loadJsPDF();
            if (!loaded) {
                setError("Failed to load PDF generator. Please check your internet.");
                return;
            }

            const { jsPDF } = window.jspdf;
            const doc = new jsPDF();
            const invoiceNo = orderData?.orderId ? `INV-${new Date().getFullYear()}-${String(orderData.orderId).padStart(5, '0')}` : 'INV-DOC';
            const date = new Date().toLocaleDateString();

            // --- Header ---
            const logoPng = await getRwLogoPng();
            if (logoPng) {
                // Aspect ratio of 327 x 193 = 1.694 (width 30mm, height 17.7mm)
                doc.addImage(logoPng, "PNG", 14, 14, 30, 17.7);
            } else {
                doc.setFontSize(22);
                doc.setTextColor(5, 59, 132); // #053b84
                doc.text("ResearchWire", 14, 22);

                doc.setFontSize(10);
                doc.setTextColor(100);
                doc.text("Market Intelligence & Research", 14, 28);
            }

            doc.setFontSize(18);
            doc.setTextColor(40);
            doc.text("PAYMENT RECEIPT", 140, 22);

            // --- Invoice Info ---
            doc.setFontSize(10);
            doc.setTextColor(40);
            doc.text(`Receipt No: ${invoiceNo}`, 140, 32);
            doc.text(`Date: ${date}`, 140, 38);

            // --- Bill To ---
            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text("Bill To:", 14, 50);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text(`${formData.firstName} ${formData.lastName}`, 14, 56);
            doc.text(formData.companyName, 14, 61);
            doc.text(formData.businessEmail, 14, 66);
            doc.text(formData.billingAddress || selectedCountry, 14, 71);

            // --- Company Info ---
            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text("From:", 140, 50);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text("Researchwire", 140, 56);
            doc.text("https://www.rwmarketintelligence.com/", 140, 61);
            doc.text("Contact: query@researchwire.in", 140, 66);

            // --- Table ---
            const tableData = [
                [
                    report?.title || "Market Report",
                    selectedRegion.name,
                    selectedFormat === 'excel_only' ? 'Excel Only' : selectedFormat === 'pdf' ? 'Single User' : selectedFormat === 'excel' ? 'Multi User' : 'Enterprise',
                    `${selectedCurrency?.code || 'INR'} ${finalPrice.toLocaleString()}`
                ]
            ];

            doc.autoTable({
                startY: 85,
                head: [['Report Title', 'Region', 'License Type', 'Total']],
                body: tableData,
                headStyles: { fillColor: [5, 59, 132] },
                margin: { left: 14, right: 14 }
            });

            // --- Final Totals ---
            const finalY = doc.lastAutoTable.finalY + 10;
            doc.setFontSize(12);
            doc.setFont("helvetica", "bold");
            doc.text(`Total Paid: ${selectedCurrency?.symbol || '₹'}${finalPrice.toLocaleString()}`, 140, finalY + 10);

            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(100);
            doc.text("Payment Status: SUCCESSFUL", 14, finalY + 10);

            // --- Footer ---
            doc.setFontSize(9);
            doc.text("The report will be delivered to your email within 2-3 business working days.", 14, finalY + 30);
            doc.text("Thank you for choosing ResearchWire.", 14, finalY + 35);

            doc.save(`${invoiceNo}.pdf`);
        } catch (err) {
            console.error("PDF Generation Error:", err);
            setError("Failed to generate receipt. Please try again.");
        } finally {
            setDownloadLoading(false);
        }
    };

    const logPaymentFailure = async (reason, rzpOrderId = null, localOrderId = null) => {
        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/payments/log-failure`, {
                orderId: localOrderId,
                razorpayOrderId: rzpOrderId,
                reason: reason
            });
        } catch (err) {
            console.warn("Failed to log payment failure on server:", err.message);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        let currentOrderId = null;
        let currentRazorpayOrderId = null;

        try {
            const [orderRes, configRes] = await Promise.all([
                axios.post(`${process.env.REACT_APP_API_BASE_URL}/payments/create-order`, {
                    reportId: report?.reportId || report?.id,
                    reportTitle: report?.title,
                    slug: report?.slug,
                    region: selectedRegion.name,
                    format: selectedFormat,
                    currency: selectedCurrency?.code,
                    currencyRate: selectedCurrency.rate,
                    customer: {
                        ...formData,
                        fullName: `${formData.firstName} ${formData.lastName}`
                    }
                }),
                axios.get(`${process.env.REACT_APP_API_BASE_URL}/payments/config`)
            ]);

            const { razorpayOrderId, amount, currency, orderId } = orderRes.data;
            currentOrderId = orderId;
            currentRazorpayOrderId = razorpayOrderId;
            const { razorpayKeyId } = configRes.data;

            const res = await loadRazorpayScript();
            if (!res || !window.Razorpay) {
                const msg = 'Razorpay SDK failed to load. Please check your internet connection or disable any ad-blockers.';
                setError(msg);
                setLoading(false);
                logPaymentFailure(msg, currentRazorpayOrderId, currentOrderId);
                return;
            }

            const options = {
                key: razorpayKeyId || 'rzp_test_demo',
                amount: amount,
                currency: currency,
                name: 'ResearchWire',
                description: `Purchase: ${report.title}`,
                image: window.location.origin + '/Rwirelogo.png',
                order_id: razorpayOrderId,
                handler: async function (response) {
                    try {
                        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/payments/verify-payment`, {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature
                        });
                        setOrderData({ orderId });
                        setSuccess(true);
                        setLoading(false);

                        // Save to localStorage with complete order metadata
                        const purchasedKey = 'rwire_purchased_reports';
                        const existingPurchases = JSON.parse(localStorage.getItem(purchasedKey) || '[]');
                        const newOrderRecord = {
                            id: orderId ? `ORD-${orderId}` : `ORD-${Math.floor(10000 + Math.random() * 90000)}`,
                            invoiceNo: orderId ? `INV-${new Date().getFullYear()}-${String(orderId).padStart(5, '0')}` : `INV-${new Date().getFullYear()}-${Math.floor(10000 + Math.random() * 90000)}`,
                            reportId: report.id,
                            reportTitle: report.title,
                            slug: report.slug || report.id,
                            category: report.category || report.industry || "Market Intelligence",
                            license: selectedFormat === 'excel_only' ? 'Excel Only License' : selectedFormat === 'pdf' ? 'Single User License' : selectedFormat === 'excel' ? 'Multi User License' : 'Enterprise License',
                            format: selectedFormat,
                            amount: `${selectedCurrency?.symbol || '$'}${finalPrice.toLocaleString()} ${selectedCurrency?.code || 'USD'}`,
                            amountPaid: finalPrice,
                            currency: selectedCurrency?.code || 'USD',
                            email: formData.businessEmail,
                            customerName: `${formData.firstName} ${formData.lastName}`,
                            companyName: formData.companyName,
                            status: 'Completed',
                            date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }),
                            purchasedAt: new Date().toISOString()
                        };
                        existingPurchases.unshift(newOrderRecord);
                        localStorage.setItem(purchasedKey, JSON.stringify(existingPurchases));
                        window.dispatchEvent(new Event('rwire_purchased_reports_updated'));

                        // Automatically purge purchased report from saved reports
                        try {
                            const savedList = JSON.parse(localStorage.getItem('rw_saved_reports') || '[]');
                            const repTitleNorm = (report.title || '').trim().toLowerCase();
                            const repIdStr = String(report.reportId || report.id || '');
                            const updatedSaved = savedList.filter(item => {
                                const itemIdStr = String(item.reportId || item.id || '');
                                if (repIdStr && itemIdStr && repIdStr === itemIdStr) return false;
                                const itemTitle = (item.title || item.reportTitle || '').trim().toLowerCase();
                                if (repTitleNorm && itemTitle && itemTitle === repTitleNorm) return false;
                                return true;
                            });
                            localStorage.setItem('rw_saved_reports', JSON.stringify(updatedSaved));
                            window.dispatchEvent(new Event('rw_saved_reports_updated'));

                            // Sync deletion to backend
                            const userEmail = formData.businessEmail || (JSON.parse(localStorage.getItem('rw_end_user') || '{}'))?.email;
                            if (userEmail) {
                                axios.post(`${process.env.REACT_APP_API_BASE_URL || '/api'}/user/delete-saved-reports`, {
                                    email: userEmail,
                                    reportId: report.id,
                                    reportTitle: report.title
                                }).catch(e => console.warn("Failed to remove saved report on purchase:", e));
                            }
                        } catch (e) {
                            console.warn("Could not purge purchased report from saved reports:", e);
                        }

                        axios.post(`${process.env.REACT_APP_API_BASE_URL}/inquiries`, {
                            ...formData,
                            fullName: `${formData.firstName} ${formData.lastName}`,
                            reportId: report.id,
                            reportTitle: report.title,
                            type: 'purchase_complete',
                            paymentId: response.razorpay_payment_id,
                            amount: finalPrice,
                            currency: selectedCurrency?.code,
                            format: selectedFormat,
                            region: selectedRegion.name
                        }).catch(err => console.warn("Note: Failed to log purchase inquiry on server, but payment was successful.", err.message));
                    } catch (err) {
                        const msg = err.response?.data?.error || 'Payment verification failed.';
                        setError(msg);
                        setLoading(false);
                        logPaymentFailure('Verification Error: ' + msg, response.razorpay_order_id, orderId);
                    }
                },
                prefill: {
                    name: `${formData.firstName} ${formData.lastName}`,
                    email: formData.businessEmail,
                    contact: formData.phoneNumber
                },
                theme: { color: '#053b84' },
                retry: { enabled: false }, 
                modal: {
                    backdropclose: false,
                    escape: false,
                    handleback: false,
                    ondismiss: function () {
                        setLoading(false);
                        console.log("Payment window dismissed by user");
                        logPaymentFailure('User closed the Razorpay payment window', currentRazorpayOrderId, currentOrderId);
                    }
                }
            };
            const rzp = new window.Razorpay(options);
            
            rzp.on('payment.failed', function (response) {
                const error = response.error;
                const reason = `Gateway Error: ${error.code} - ${error.description}`;
                setError(error.description);
                logPaymentFailure(reason, currentRazorpayOrderId, currentOrderId);
            });

            rzp.open();
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to initialize payment.';
            setError(msg);
            setLoading(false);
            logPaymentFailure('Initialization Error: ' + msg, currentRazorpayOrderId, currentOrderId);
        }
    };

    const handleRegionChange = (e) => {
        const region = availableRegions.find(r => r.name === e.target.value);
        if (region) {
            setSelectedRegion(region);
            setSelectedCountry('');
        }
    };

    if (!show) return null;

    const renderProgressBar = () => (
        <div className="d-flex justify-content-center align-items-center mb-3 gap-3">
            {[1, 2, 3].map((s) => (
                <div key={s} className="d-flex align-items-center gap-2">
                    <div
                        className={`rounded-circle d-flex align-items-center justify-content-center fw-bold transition-all`}
                        style={{
                            width: '28px',
                            height: '28px',
                            fontSize: '0.8rem',
                            backgroundColor: (success || step >= s) ? '#053b84' : '#E1E7EF',
                            color: (success || step >= s) ? 'white' : '#64748B',
                            border: (step === s && !success) ? '3px solid #D1E9FF' : 'none'
                        }}
                    >
                        {(success || step > s) ? <IoCheckmarkCircle size={18} /> : s}
                    </div>
                    {s < 3 && <div className="bg-light" style={{ width: '30px', height: '2px' }}></div>}
                </div>
            ))}
        </div>
    );

    const renderPriceSummary = () => (
        <div className="p-2 px-3 bg-light rounded-3 mt-3" style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
            <div className="d-flex justify-content-between mb-2">
                <span className="text-secondary small">Currency</span>
                <span className="fw-bold small">{selectedCurrency?.code || '---'}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
                <span className="text-secondary small">License</span>
                <span className="fw-bold small">
                    {selectedFormat === 'excel_only' ? 'Excel Only' : selectedFormat === 'pdf' ? 'Single User' : selectedFormat === 'excel' ? 'Multi User' : 'Enterprise'}
                </span>
            </div>
            <div className="d-flex justify-content-between mb-1">
                <span className="text-secondary small">Subtotal</span>
                <span className="fw-bold small">{selectedCurrency?.symbol || ''}{finalPrice.toLocaleString()}</span>
            </div>
            <div className="d-flex justify-content-between mb-1">
                <span className="text-secondary small">Tax (0%)</span>
                <span className="fw-bold small">{selectedCurrency?.symbol || ''}0</span>
            </div>
            <hr className="my-2" />
            <div className="d-flex justify-content-between">
                <span className="fw-bold">Total</span>
                <span className="fw-bold text-primary" style={{ fontSize: '1.1rem' }}>{selectedCurrency?.symbol || ''}{finalPrice.toLocaleString()}</span>
            </div>
        </div>
    );

    return (
        <div className="modal show" style={{ display: (loading && step === 2 && !success) ? 'none' : 'block', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1070, padding: '20px' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 p-3" style={{ borderRadius: '24px', maxWidth: '580px', margin: 'auto', maxHeight: '92vh', overflowY: 'auto' }}>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <h5 className="fw-bold m-0" style={{ fontSize: '1.2rem' }}>
                            {success ? 'Order Confirmed!' : step === 1 ? 'Purchase Report' : 'Billing Details'}
                        </h5>
                        <button className="btn p-0" onClick={onClose} disabled={loading}>
                            <IoCloseOutline size={28} />
                        </button>
                    </div>
                    <p className="text-secondary small mb-3">
                        {success ? 'Thank you for your purchase!' : step === 1 ? 'Select your region and license type' : 'Enter your billing information'}
                    </p>

                    {renderProgressBar()}

                    <div style={{ marginTop: '-8px' }}>
                        {!success ? (
                            <>
                                {/* Report Banner */}
                                <div className="mb-2 p-2 px-3 d-flex align-items-center gap-3 commonBorder" style={{ backgroundColor: '#F3F4F64D', borderRadius: '12px' }}>
                                    <div className="overflow-hidden">
                                        <p className="fw-bold text-primary mb-0 text-truncate small" style={{ maxWidth: '100%', fontSize: '0.8rem' }}>{report?.title}</p>
                                    </div>
                                </div>

                                {alreadyPurchased && !ignoreWarning && (
                                    <div className="animate-fade-in mb-3 p-3 text-start" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '12px' }}>
                                        <div className="d-flex align-items-center gap-2 mb-2 text-warning">
                                            <IoShieldCheckmarkOutline size={20} />
                                            <h6 className="fw-bold mb-0">Previous Purchase Found</h6>
                                        </div>
                                        <p className="small text-secondary mb-3">
                                            You already bought <strong>"{alreadyPurchased.reportTitle}"</strong> for <strong>{alreadyPurchased.license}</strong>. 
                                            Would you want to buy this report again for <strong>{selectedFormat === 'excel_only' ? 'Excel Only License' : selectedFormat === 'pdf' ? 'Single User License' : selectedFormat === 'excel' ? 'Multi User License' : 'Enterprise License'}</strong>?
                                        </p>
                                        <div className="d-flex gap-2">
                                            <button 
                                                className="btn btn-warning flex-grow-1 py-2 fw-bold text-white" 
                                                style={{ borderRadius: '10px' }}
                                                onClick={() => setIgnoreWarning(true)}
                                            >
                                                Yes, Buy Again
                                            </button>
                                            <button 
                                                className="btn btn-light flex-grow-1 py-2 fw-bold" 
                                                style={{ borderRadius: '10px' }}
                                                onClick={onClose}
                                            >
                                                No, Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {(!alreadyPurchased || ignoreWarning) && step === 1 && (
                                    <div className="step-content animate-fade-in">
                                        <div className="mb-2">
                                            <div className="p-2 px-3 bg-white" style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                                                <div className="row g-2 align-items-center">
                                                    <div className="col-6">
                                                        <label className="form-label x-small fw-bold mb-1">Currency</label>
                                                        <select
                                                            className="form-select border-0 bg-light shadow-none py-1"
                                                            style={{ fontSize: '0.85rem', fontWeight: 'bold', borderRadius: '6px' }}
                                                            value={selectedCurrency?.code || ''}
                                                            onChange={e => setSelectedCurrency(availableCurrencies.find(c => c.code === e.target.value) || availableCurrencies[0])}
                                                        >
                                                            {availableCurrencies.map(curr => <option key={curr.code} value={curr.code}>{curr.code}</option>)}
                                                        </select>
                                                    </div>
                                                    <div className="col-6">
                                                        <label className="form-label x-small fw-bold mb-1">Region</label>
                                                        <select
                                                            className="form-select bg-light border-0 shadow-none py-1"
                                                            style={{ borderRadius: '6px', fontSize: '0.85rem', fontWeight: 'bold' }}
                                                            value={selectedRegion.name}
                                                            onChange={handleRegionChange}
                                                        >
                                                            {availableRegions.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center mt-2">
                                                    <p className="text-danger x-small mb-0" style={{ fontWeight: '500' }}>
                                                        Paying in {selectedCurrency?.code}
                                                    </p>
                                                    <div className="d-flex align-items-center gap-1 text-success px-2 py-0.5 rounded-pill border fw-bold" style={{ backgroundColor: '#F8F9FA', fontSize: '0.6rem', borderColor: '#E1E7EF' }}>
                                                        <IoShieldCheckmarkOutline size={10} /> PCI DSS
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <label className="form-label x-small fw-bold mb-1">License Type</label>
                                        <div className="d-flex flex-column gap-2">
                                            {[
                                                { id: 'excel_only', title: 'Excel Only', desc: 'Excel data sheet only', amount: 'excelOnly' },
                                                { id: 'pdf', title: 'Single User License', desc: 'For individual use only', amount: 'single' },
                                                { id: 'excel', title: 'Multi User License', desc: 'Up to 5 users in your team', amount: 'multi' },
                                                { id: 'both', title: 'Enterprise License', desc: 'Unlimited users, company-wide', amount: 'enterprise' }
                                            ].filter((lic) => {
                                                if (lic.id === 'excel_only') {
                                                    let pricing = report?.pricingOptions || report?.pricing_options || {};
                                                    if (typeof pricing === 'string') {
                                                        try { pricing = JSON.parse(pricing); } catch (e) { pricing = {}; }
                                                    }
                                                    const g = pricing.global || {};
                                                    return g.excelOnly && parseFloat(g.excelOnly) > 0;
                                                }
                                                return true;
                                            }).map((lic) => {
                                                let p = 0;
                                                let pricing = report?.pricingOptions || report?.pricing_options || {};
                                                if (typeof pricing === 'string') {
                                                    try { pricing = JSON.parse(pricing); } catch (e) { pricing = {}; }
                                                }
                                                if (pricing.enableRegional && selectedRegion.id !== 'global') {
                                                    const rp = pricing.regions?.find(r => r.region === selectedRegion.name);
                                                    if (rp) p = parseFloat(lic.id === 'excel_only' ? rp.excelOnly : lic.id === 'pdf' ? rp.singleUser : lic.id === 'excel' ? rp.multiUser : rp.enterprise);
                                                } else {
                                                    const g = pricing.global || {};
                                                    p = parseFloat(lic.id === 'excel_only' ? g.excelOnly : lic.id === 'pdf' ? g.singleUser : lic.id === 'excel' ? g.multiUser : g.enterprise);
                                                }
                                                let options = report?.pricingOptions || report?.pricing_options || {};
                                                if (typeof options === 'string') {
                                                    try { options = JSON.parse(options); } catch (e) { options = {}; }
                                                }
                                                const globalOptions = options.global || {};
                                                const reportCurrencyCode = globalOptions.currency || 'USD';

                                                // Find report base currency rate from loaded list
                                                const reportBaseCurrency = availableCurrencies.find(c => c.code === reportCurrencyCode) || { rate: 1 };

                                                // Converted price = price * (userSelectedCurrencyRate / reportBaseCurrencyRate)
                                                const dispPrice = Math.round(p * ((selectedCurrency?.rate || 1) / (reportBaseCurrency.rate || 1)));

                                                return (
                                                    <div
                                                        key={lic.id}
                                                        onClick={() => setSelectedFormat(lic.id)}
                                                        className={`p-1 px-3 rounded-4 border transition-all cursor-pointer d-flex justify-content-between align-items-center ${selectedFormat === lic.id ? 'border-primary' : 'border-light'}`}
                                                        style={{
                                                            backgroundColor: selectedFormat === lic.id ? '#F0F7FF' : 'white',
                                                            boxShadow: selectedFormat === lic.id ? '0 4px 12px rgba(5, 59, 132, 0.08)' : 'none'
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center gap-2">
                                                            <input
                                                                type="radio"
                                                                checked={selectedFormat === lic.id}
                                                                onChange={() => { }}
                                                                className="form-check-input m-0 cursor-pointer"
                                                                style={{ width: '14px', height: '14px' }}
                                                            />
                                                            <div>
                                                                <h6 className="fw-bold mb-0" style={{ fontSize: '0.85rem' }}>{lic.title}</h6>
                                                                <p className="text-secondary x-small mb-0">{lic.desc}</p>
                                                            </div>
                                                        </div>
                                                        <div className="fw-bold text-primary" style={{ fontSize: '0.9rem' }}>
                                                            {selectedCurrency.symbol}{dispPrice.toLocaleString()}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {renderPriceSummary()}

                                        <button
                                            className="btn btn-primary w-100 mt-2 py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
                                            style={{ background: "linear-gradient(0deg, #012970, #024ed6)", borderRadius: '12px', border: 'none' }}
                                            onClick={() => setStep(2)}
                                        >
                                            Continue to Billing <IoChevronForward />
                                        </button>
                                    </div>
                                )}

                                {(!alreadyPurchased || ignoreWarning) && step === 2 && (
                                    <form onSubmit={handleSubmit} className="step-content animate-fade-in">
                                        {loading ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}></div>
                                                <h5 className="fw-bold">Awaiting Payment...</h5>
                                                <p className="text-muted small">Please complete your payment in the secure Razorpay window.</p>
                                            </div>
                                        ) : (
                                            <div className="row g-3">
                                                <div className="col-md-6 text-start">
                                                    <label className="form-label small fw-bold mb-1">First Name</label>
                                                    <input type="text" className="form-control bg-light border-0 py-2" placeholder="John" required value={formData.firstName} onChange={e => setFormData({ ...formData, firstName: e.target.value })} />
                                                </div>
                                                <div className="col-md-6 text-start">
                                                    <label className="form-label small fw-bold mb-1">Last Name</label>
                                                    <input type="text" className="form-control bg-light border-0 py-2" placeholder="Doe" required value={formData.lastName} onChange={e => setFormData({ ...formData, lastName: e.target.value })} />
                                                </div>
                                                <div className="col-12 text-start">
                                                    <label className="form-label small fw-bold mb-1">Company Name</label>
                                                    <input type="text" className="form-control bg-light border-0 py-2" placeholder="Abcd Inc." required value={formData.companyName} onChange={e => setFormData({ ...formData, companyName: e.target.value })} />
                                                </div>
                                                <div className="col-12 text-start">
                                                    <label className="form-label small fw-bold mb-1">Work Email</label>
                                                    <input type="email" className="form-control bg-light border-0 py-2" placeholder="john@abcd.com" required value={formData.businessEmail} onChange={e => setFormData({ ...formData, businessEmail: e.target.value })} />
                                                </div>
                                                <div className="col-md-6 text-start">
                                                    <label className="form-label small fw-bold mb-1">Phone</label>
                                                    <input type="tel" className="form-control bg-light border-0 py-2" placeholder="+1 (555) 000-0000" required value={formData.phoneNumber} onChange={e => setFormData({ ...formData, phoneNumber: e.target.value })} />
                                                </div>
                                                <div className="col-md-6 text-start">
                                                    <label className="form-label small fw-bold mb-1">Country</label>
                                                    <input type="text" className="form-control bg-light border-0 py-2" placeholder="e.g. India" required value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)} />
                                                </div>
                                                <div className="col-12 text-start">
                                                    <label className="form-label small fw-bold mb-1">Billing Address</label>
                                                    <input type="text" className="form-control bg-light border-0 py-2" placeholder="123 Business Abcd" required value={formData.billingAddress} onChange={e => setFormData({ ...formData, billingAddress: e.target.value })} />
                                                </div>
                                            </div>
                                        )}

                                        <div className="mt-4 p-3 bg-white border border-light shadow-sm rounded-3 d-flex justify-content-between align-items-center">
                                            <span className="fw-bold">Total Amount</span>
                                            <span className="fw-bold text-primary" style={{ fontSize: '1.1rem' }}>{selectedCurrency.symbol}{finalPrice.toLocaleString()}</span>
                                        </div>

                                        {error && <div className="alert alert-danger mt-3 py-2 small">{error}</div>}

                                        <div className="d-flex gap-3 mt-4">
                                            <button type="button" className="btn btn-light flex-grow-1 py-2 fw-bold" onClick={() => setStep(1)} style={{ borderRadius: '12px' }}>Back</button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary flex-grow-1 py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
                                                style={{ background: "linear-gradient(0deg, #012970, #024ed6)", borderRadius: '12px', border: 'none' }}
                                                disabled={loading}
                                            >
                                                {loading ? <span className="spinner-border spinner-border-sm"></span> : 'Proceed to Payment'}
                                                {!loading && <IoChevronForward />}
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-2 step-content animate-fade-in">
                                <div className="mb-3 d-flex justify-content-center">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '64px', height: '64px', backgroundColor: '#E8F8F0' }}>
                                        <IoCheckmarkCircle size={40} className="text-success" />
                                    </div>
                                </div>
                                <h4 className="fw-bold mb-1">Payment Successful!</h4>
                                <p className="text-secondary small mb-3">Your order has been confirmed.</p>

                                <div className="p-3 bg-light rounded-4 mb-3 text-start border border-light">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-secondary small">Invoice No.</span>
                                        <span className="fw-bold small">{orderData?.orderId ? `INV-${new Date().getFullYear()}-${String(orderData.orderId).padStart(5, '0')}` : 'INV-2025-00847'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-secondary small">Amount Paid</span>
                                        <span className="fw-bold small">{selectedCurrency.symbol}{finalPrice.toLocaleString()}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span className="text-secondary small">License</span>
                                        <span className="fw-bold small">{selectedFormat === 'excel_only' ? 'Excel Only License' : selectedFormat === 'pdf' ? 'Single User License' : selectedFormat === 'excel' ? 'Multi User License' : 'Enterprise License'}</span>
                                    </div>
                                </div>

                                <p className="text-muted small mb-3 px-3" style={{ fontSize: '0.75rem' }}>
                                    A confirmation email has been sent to your email id. The report will be delivered to your email id within the next 2–3 business working days.
                                </p>

                                <div className="d-flex gap-2">
                                    <button className="btn btn-light flex-grow-1 py-2 fw-bold" onClick={onClose} style={{ borderRadius: '12px' }}>Close</button>
                                    <button
                                        onClick={handleDownload}
                                        className="btn btn-primary flex-grow-1 py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
                                        style={{ backgroundColor: '#053b84', borderRadius: '12px', border: 'none' }}
                                        disabled={downloadLoading}
                                    >
                                        {downloadLoading ? <span className="spinner-border spinner-border-sm"></span> : <IoDownloadOutline size={18} />}
                                        Download Receipt
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PurchaseModal;
